import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, Download, Loader2 } from 'lucide-react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { useCart } from '@/context/CartContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Success = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { refreshCart } = useCart();
  const [status, setStatus] = useState('checking');
  const [paymentInfo, setPaymentInfo] = useState(null);
  const [attempts, setAttempts] = useState(0);
  const maxAttempts = 5;

  // Stripe returns ?session_id=…; PayPal returns ?token=<order id>&PayerID=….
  const sessionId = searchParams.get('session_id') || searchParams.get('token');

  useEffect(() => {
    if (!sessionId) {
      navigate('/');
      return;
    }

    checkPaymentStatus();
  }, [sessionId]);

  const checkPaymentStatus = async () => {
    try {
      const response = await axios.get(`${API}/checkout/status/${sessionId}`);
      
      if (response.data.payment_status === 'paid') {
        setStatus('success');
        setPaymentInfo(response.data);
        refreshCart();
      } else if (response.data.status === 'expired') {
        setStatus('expired');
      } else if (attempts < maxAttempts) {
        setAttempts(prev => prev + 1);
        setTimeout(checkPaymentStatus, 2000);
      } else {
        setStatus('timeout');
      }
    } catch (error) {
      console.error('Error checking payment status:', error);
      if (attempts < maxAttempts) {
        setAttempts(prev => prev + 1);
        setTimeout(checkPaymentStatus, 2000);
      } else {
        setStatus('error');
      }
    }
  };

  if (status === 'checking') {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh]" data-testid="payment-checking">
        <Loader2 className="w-16 h-16 text-primary animate-spin mb-6" />
        <h2 className="text-2xl font-bold text-foreground mb-2">Processing Payment...</h2>
        <p className="text-muted-foreground">Please wait while we confirm your payment</p>
      </div>
    );
  }

  if (status === 'success') {
    return (
      <div className="max-w-3xl mx-auto px-6 py-12">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-card border border-border/50 rounded-3xl p-12 text-center"
          data-testid="payment-success"
        >
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: 'spring' }}
          >
            <CheckCircle className="w-24 h-24 text-secondary mx-auto mb-6" />
          </motion.div>
          
          <h1 className="text-4xl md:text-5xl font-bold font-display text-foreground mb-4">
            Payment Successful!
          </h1>
          <p className="text-lg text-muted-foreground mb-8">
            Thank you for your purchase. You now have instant access to your music.
          </p>

          {paymentInfo && (
            <div className="bg-background rounded-2xl p-6 mb-8 text-left max-w-md mx-auto">
              <h3 className="font-bold text-foreground mb-4">Order Details</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Amount Paid:</span>
                  <span className="font-bold text-foreground" data-testid="payment-amount">
                    ${paymentInfo.amount.toFixed(2)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Currency:</span>
                  <span className="font-bold text-foreground uppercase">
                    {paymentInfo.currency}
                  </span>
                </div>
              </div>
            </div>
          )}

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => navigate('/library')}
              className="flex items-center justify-center gap-2 px-8 py-4 bg-primary hover:bg-primary/90 text-primary-foreground rounded-full font-bold text-lg shadow-glow transition-all hover:scale-105"
              data-testid="view-library-btn"
            >
              <Download className="w-5 h-5" />
              View My Library
            </button>
            <button
              onClick={() => navigate('/')}
              className="px-8 py-4 bg-secondary/20 hover:bg-secondary/30 text-secondary-foreground border border-secondary/50 rounded-full font-bold text-lg transition-all hover:scale-105"
              data-testid="browse-more-btn"
            >
              Browse More Music
            </button>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-6 py-20 text-center" data-testid="payment-error">
      <h2 className="text-3xl font-bold font-display text-foreground mb-4">
        {status === 'expired' ? 'Payment Session Expired' : 'Payment Status Unknown'}
      </h2>
      <p className="text-lg text-muted-foreground mb-8">
        {status === 'expired'
          ? 'Your payment session has expired. Please try again.'
          : 'We were unable to confirm your payment status. Please check your email or contact support.'}
      </p>
      <button
        onClick={() => navigate('/')}
        className="px-8 py-4 bg-primary hover:bg-primary/90 text-primary-foreground rounded-full font-bold text-lg shadow-glow transition-all hover:scale-105"
        data-testid="back-home-btn"
      >
        Back to Store
      </button>
    </div>
  );
};